import { Box, Spinner } from "@chakra-ui/react";
//
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
//
import InlineFilterPopover from "../InlineFilterPopover";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";

// Components
import NotesTile from "../NotesTile";
import NoteInput from "./AddNotesInput";

// Hooks
import usePermission from "../../hooks/usePermission";
import useClaimsToast from "../../hooks/useToast";

// Protos
import { NoteTypeName } from "../../utils/api/protos/messages/claims.message_pb";

// Providers
import { useClaimDetails } from "../../context-providers/claims-details-provider";

const NoteDetails: FunctionComponent = (): ReactElement => {
	const { id } = useParams();
	const {
		notes,
		pinnedNotes,
		handleCreateNote,
		handleFetchPinnedNotes,
		handleFetchActivities,
		handleFetchNotes,
		hasMoreNotes,
		handleLoadMoreNotes,
		handlePinNote,
		handleUnpinNote,
		isLoading
	} = useClaimDetails();
	const [newNote, setNewNote] = useState<string>("");
	const [noteType, setNoteType] = useState<NoteTypeName>(NoteTypeName.NOTE_TYPE_GENERAL);
	const { hasGeneralUpdateAccess } = usePermission();
	const toast = useClaimsToast();

	// Filter state (mirrors Activity filter behavior)
	const [filterNoteType, setFilterNoteType] = useState<string>("");
	const [appliedFilterNoteType, setAppliedFilterNoteType] = useState<string>("");
	const [filterRole, setFilterRole] = useState<string>("");
	const [appliedFilterRole, setAppliedFilterRole] = useState<string>("");
	const [showFilterMenu, setShowFilterMenu] = useState(false);

	useEffect(() => {
		if (id) handleFetchPinnedNotes(Number(id));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// Handle adding a new note and refetching activities
	const handleAddNote = async () => {
		if (!newNote.trim()) {
			toast("Error", "Note cannot be empty", "error");
			return;
		}
		try {
			await handleCreateNote(Number(id), newNote, noteType);
			await handleFetchActivities();
			setNewNote("");
			toast("Success", "Note added successfully", "success");
		} catch (e) {
			console.error(e);
			toast("Error", "Unable to create note", "error");
		}
	};

	const clearFilter = () => {
		setShowFilterMenu(false);
		setFilterNoteType("");
		setAppliedFilterNoteType("");
		setFilterRole("");
		setAppliedFilterRole("");
		// Refetch notes without filters
		if (id) handleFetchNotes(Number(id), "", "");
	};

	const shownNotesText = useMemo(() => {
		if (appliedFilterNoteType === "" && appliedFilterRole === "") return "All";
		if (appliedFilterNoteType !== "" && appliedFilterRole !== "") return "Role & Note Type";
		if (appliedFilterNoteType === "" && appliedFilterRole !== "") return `${appliedFilterRole} Role`;
		if (appliedFilterNoteType !== "" && appliedFilterRole === "") return `${appliedFilterNoteType} Type`;
		return "All";
	}, [appliedFilterRole, appliedFilterNoteType]);

	// Build note type options dynamically from fetched notes (including pinned)
	const noteTypeOptions = useMemo(() => {
		const labelsSet = new Set<string>();
		(pinnedNotes ?? []).forEach((n) => {
			if (n.type?.label) labelsSet.add(String(n.type.label));
		});
		(notes ?? []).forEach((n) => {
			if (n.type?.label) labelsSet.add(String(n.type.label));
		});
		return Array.from(labelsSet)
			.sort((a, b) => a.localeCompare(b))
			.map((label) => ({ value: label, label }));
	}, [notes, pinnedNotes]);

	return (
		<div style={{ margin: "0 auto", fontFamily: "Arial, sans-serif" }}>
			{/* Header removed; filter moved below Post button */}
			{/* Input Section */}
			<div style={{ marginBottom: "24px" }}>
				<NoteInput
					noteType={noteType}
					setNoteType={setNoteType}
					newNote={newNote}
					setNewNote={setNewNote}
					handleAddNote={handleAddNote}
				/>
			</div>

			{/* Filter Button and Popup under Post */}
			<InlineFilterPopover
				shownText={shownNotesText}
				isFiltered={!(appliedFilterNoteType === "" && appliedFilterRole === "")}
				onClear={clearFilter}
				roleValue={filterRole}
				onRoleChange={(v) => setFilterRole(v)}
				typeLabel="Note Type"
				typeValue={filterNoteType}
				onTypeChange={(v) => setFilterNoteType(v)}
				typeOptions={noteTypeOptions}
				onApply={async () => {
					setAppliedFilterNoteType(filterNoteType);
					setAppliedFilterRole(filterRole);
					// Refetch notes with filters
					if (id) await handleFetchNotes(Number(id), filterRole || undefined, filterNoteType || undefined);
				}}
			/>
			{/* Small popover content is already rendered above via Popover */}
			<Box style={{ display: showFilterMenu ? "block" : "none" }} />
			{/* Keeping structure simple; popover handles visibility */}
			{/* Content (duplicated in PopoverBody) retained only for readability */}
			{/* End popover section */}
			<Box p="0" />

			{pinnedNotes?.length > 0 && (
				<NotesTile
					isPinned={true}
					isPinDisabled={!hasGeneralUpdateAccess}
					data={pinnedNotes}
					noDataMessage=""
					handlePinNote={handlePinNote}
					handleUnpinNote={handleUnpinNote}
				/>
			)}

			{/* Notes List */}
			{isLoading ? (
				<Box display="flex" justifyContent="center" py={2}>
					<Spinner size="sm" color="orange.500" />
				</Box>
			) : (
				<InfiniteScroll
					dataLength={notes?.length ?? 0}
					next={() => handleLoadMoreNotes(appliedFilterRole || undefined, appliedFilterNoteType || undefined)}
					hasMore={hasMoreNotes}
					loader={
						<Box display="flex" justifyContent="center" py={2}>
							<Spinner size="sm" color="orange.500" />
						</Box>
					}
					scrollableTarget="pageBody">
					<NotesTile
						isPinned={false}
						isPinDisabled={!hasGeneralUpdateAccess}
						data={notes ?? []}
						noDataMessage="No notes available"
						handlePinNote={handlePinNote}
						handleUnpinNote={handleUnpinNote}
					/>
				</InfiniteScroll>
			)}
		</div>
	);
};

export default NoteDetails;
